import ItiButton from "@/iti-core-react/components/ItiButton/ItiButton";
import { useLanguage } from "@/iti-core-react/hooks/useLanguage";
import { NotificationContext } from "@/shared/components/NotificationsPanel/NotificationContext";
import { PersonalNotificationDto } from "@/shared/dtos/personal-notifications-dto";
import { getNotificationIcon } from "@/shared/enums/notification-utils";
import { RootState } from "@/store";
import { showNavigationBlockedMessage } from "@/store/navigationBlock/navigationBlockActions";
import Button from "devextreme-react/button";
import { ContextMenu } from "devextreme-react/context-menu";
import { useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import "./NotificationsPanelTest.scss";

interface NotificationMenuItem {
	id: string;
	text: string;
	icon: string;
	reason: string;
	isSeeMore?: boolean;
}

export default function NotificationPanel() {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const { t } = useLanguage();
	const isNavigationBlocked = useSelector(
		(state: RootState) => state.navigationBlock.isNavigationBlocked,
	);
	const {
		notifications,
		newNotificationsCount,
		resetNotificationsCount,
		freshIds,
		markAllSeen,
	} = useContext(NotificationContext);

	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const [notificationsMenuItems, setNotificationsMenuItems] = useState<
		NotificationMenuItem[]
	>([]);

	const notificationsRoutePath = "/notifications";

	const createMenuItem = (
		notif: PersonalNotificationDto,
	): NotificationMenuItem => ({
		id: notif.id,
		text: notif.message || t(`notifications.${notif.reason?.toLowerCase()}`),
		icon: getNotificationIcon(notif.reason),
		reason: notif.reason,
	});

	const createSeeMoreItem = (): NotificationMenuItem => ({
		id: "see-more",
		text: t("notification.see-more"),
		icon: "mdi mdi-dots-horizontal",
		reason: "",
		isSeeMore: true,
	});

	const onItemKeyDown = (
		e: React.KeyboardEvent,
		item: NotificationMenuItem,
	) => {
		if (e.key === "Enter" || e.key === " ") {
			e.preventDefault();
			handleItemClick(item);
		}
	};

	const handleSeeMoreClick = async () => {
		const currentPath = globalThis.location.pathname;
		if (isNavigationBlocked && currentPath.includes("/second-step/")) {
			const canNavigate = await dispatch(showNavigationBlockedMessage());
			if (!canNavigate) return;
		}
		navigate(notificationsRoutePath);
		setIsMenuOpen(false);
		markAllSeen();
		setTimeout(resetNotificationsCount, 300);
	};

	const handleItemClick = async (item: NotificationMenuItem) => {
		if (item?.isSeeMore) {
			handleSeeMoreClick();
			return;
		}
		const currentPath = globalThis.location.pathname;
		if (isNavigationBlocked && currentPath.includes("/second-step/")) {
			const canNavigate = await dispatch(showNavigationBlockedMessage());
			if (!canNavigate) return;
		}
		navigate(notificationsRoutePath);
		setIsMenuOpen(false);
		markAllSeen();
		setTimeout(resetNotificationsCount, 300);
	};

	// Construir TOP-5 cada vez que cambian las notificaciones globales
	useEffect(() => {
		if (!notifications || notifications.length === 0) {
			setNotificationsMenuItems([]);
			return;
		}
		const top5 = notifications.slice(0, 5);
		const items = top5.map(createMenuItem);
		if (items.length) items.push(createSeeMoreItem());
		setNotificationsMenuItems(items);
	}, [notifications]);

	const renderMenuItem = (item: NotificationMenuItem) => {
		if (item.isSeeMore) {
			return (
				<div className="row w-100 justify-content-center">
					<ItiButton
						elementAttr={{ class: "see-more" }}
						text={item.text}
						onClick={handleSeeMoreClick}
					/>
				</div>
			);
		}

		const notification = notifications.find((n) => n.id === item.id);
		const isRead = !!notification?.read;
		const isFresh = !!(notification && freshIds.has(notification.id));

		return (
			<div
				className={`row notification-item ${isRead ? "read" : "unread"} ${isFresh ? "fresh" : ""}`}
				onClick={() => handleItemClick(item)}
				onKeyDown={(e) => onItemKeyDown(e, item)}
			>
				<div className="col-auto notification-status-indicator" />
				<div className="col-auto notification-icon-container m-0 d-flex justify-content-center align-items-center">
					<i className={`${item.icon} notification-icon`} />
				</div>
				<div className="col notification-content ps-0 ms-0">
					<div className="notification-message">{item.text}</div>
					{notification?.generationDate && (
						<div className="d-flex mt-1">
							<i className="me-1 mdi mdi-clock-outline notification-clock" />
							<div className="notification-date">
								{new Date(notification.generationDate).toLocaleString()}
							</div>
						</div>
					)}
				</div>
			</div>
		);
	};

	return (
		<div className="notifications-wrapper">
			<Button
				className="notifications-panel notifications-button"
				stylingMode="text"
				onClick={() => setIsMenuOpen((prev) => !prev)}
			>
				<div className="button-content">
					{newNotificationsCount !== 0 && (
						<span className="notification-count">{newNotificationsCount}</span>
					)}
					<div className="notifications-info">
						<span className="bell mdi mdi-bell-outline" />
					</div>
				</div>
			</Button>

			<ContextMenu
				items={notificationsMenuItems || []}
				target=".notifications-button"
				visible={isMenuOpen}
				onHiding={() => setIsMenuOpen(false)}
				onShowing={() => setIsMenuOpen(true)}
				cssClass="notifications-menu"
				animation={{
					show: { type: "fade", duration: 200, from: 0, to: 1 },
					hide: { type: "fade", duration: 200, from: 1, to: 0 },
				}}
				itemRender={renderMenuItem}
			/>
		</div>
	);
}
